import React from "react";
import { FaClock, FaPhoneAlt, FaPhoneSlash, FaExclamationTriangle } from "react-icons/fa";

// Define call status types matching backend
type CallStatus =
  | "connecting"
  | "connected"
  | "ended"
  | "rejected"
  | "failed";

// Define the payload interface
interface WhatsAppCallPayload {
  provider: string;
  direction: "user_initiated" | "business_initiated";
  from: string;
  to: string;
  call_id: string;
  status: CallStatus;
  duration?: number;
  ended_at?: string;
}

// Props interface
interface WhatsAppCallCardProps {
  payload: WhatsAppCallPayload;
}

const formatDuration = (seconds: number): string => {
  if (!seconds || seconds === 0) return "";
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

const getStatusConfig = (status: CallStatus) => {
  switch (status) {
    case "connecting":
      return {
        label: "Connecting...",
        color: "#3b82f6", // blue
        bgColor: "#eff6ff", // light blue
        icon: FaPhoneAlt,
        showDuration: false
      };
    case "connected":
      return {
        label: "In Progress",
        color: "#10b981", // green
        bgColor: "#f0fdf4", // light green
        icon: FaPhoneAlt,
        showDuration: false
      };
    case "ended":
      return {
        label: "Call Ended",
        color: "#16a34a", // green
        bgColor: "#f0fdf4", // light green
        icon: FaPhoneAlt,
        showDuration: true
      };
    case "rejected":
      return {
        label: "Call Declined",
        color: "#dc2626", // red
        bgColor: "#fef2f2", // light red
        icon: FaPhoneSlash,
        showDuration: false
      };
    case "failed":
      return {
        label: "Call Failed",
        color: "#dc2626", // red
        bgColor: "#fef2f2", // light red
        icon: FaExclamationTriangle,
        showDuration: false
      };
    default:
      return {
        label: status,
        color: "#6b7280", // gray
        bgColor: "#f9fafb", // light gray
        icon: FaPhoneAlt,
        showDuration: false
      };
  }
};

const WhatsappCall: React.FC<WhatsAppCallCardProps> = ({ payload }) => {
  const statusConfig = getStatusConfig(payload.status);
  const StatusIcon = statusConfig.icon;
  const duration = formatDuration(payload.duration || 0);
  
  return (
    <div
      style={{
        maxWidth: "20rem",
        backgroundColor: statusConfig.bgColor,
        borderRadius: "8px",
        border: `1px solid ${statusConfig.color}30`,
        padding: "12px",
        boxShadow: "0 1px 4px rgba(0, 0, 0, 0.08)",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        {/* Left side: Icon + Status */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <div
            style={{
              backgroundColor: statusConfig.color,
              borderRadius: "50%",
              padding: "6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <StatusIcon color="white" size={12} />
          </div>
          <div>
            <div
              style={{
                fontWeight: 600,
                color: statusConfig.color,
                fontSize: "13px",
                lineHeight: 1,
              }}
            >
              Voice Call
            </div>
            <div
              style={{
                fontSize: "11px",
                color: statusConfig.color,
                fontWeight: 400,
                marginTop: "2px",
                lineHeight: 1,
              }}
            >
              {statusConfig.label}
            </div>
          </div>
        </div>

        {/* Right side: Duration (only for completed calls) */}
        {statusConfig.showDuration && duration && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "3px",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              padding: "3px 6px",
              borderRadius: "12px",
              fontSize: "11px",
              fontWeight: 500,
              color: "#374151",
            }}
          >
            <FaClock size={10} />
            {duration}
          </div>
        )}
      </div>
    </div>
  );
};

export default WhatsappCall;
